"use client";
import { cn } from "../../lib/utilsComprehensive";
import React, { useMemo, useState, forwardRef } from "react";
import { Motion } from "../../primitives";
import { CardContent, CardHeader, CardTitle, GlassCard } from "../card";
import {
  ContrastGuard,
  TextWithContrast,
} from "@/components/accessibility/ContrastGuard";
import { ANIMATION } from "../../tokens/designConstants";

const toFiniteNumber = (value: unknown, fallback = 0): number => {
  const numericValue =
    typeof value === "number"
      ? value
      : typeof value === "string" && value.trim() !== ""
        ? Number(value)
        : fallback;
  return Number.isFinite(numericValue) ? numericValue : fallback;
};

const getSegmentColor = (
  explicitColor: string | undefined,
  colors: string[],
  index: number
) => explicitColor || colors[index % Math.max(1, colors.length)] || "#70d6ff";

const DEFAULT_CHART_COLORS = [
  "#70d6ff",
  "#a78bfa",
  "#34d399",
  "#fbbf24",
  "#f87171",
  "#22d3ee",
  "#fb7185",
  "#c084fc",
  "#60a5fa",
  "#94a3b8",
];

const LABEL_FILL = "rgba(226, 232, 240, 0.86)";
const LABEL_MUTED_FILL = "rgba(203, 213, 225, 0.72)";
const CENTER_FILL = "rgba(248, 250, 252, 0.95)";

export interface PieDataPoint {
  label: string;
  value: number;
  color?: string;
}

export interface GlassPieChartProps {
  /**
   * Chart title
   */
  title?: string;
  /**
   * Chart data
   */
  data: PieDataPoint[];
  /**
   * Chart width and height
   */
  size?: number;
  /**
   * Inner radius for donut chart (0 for pie chart)
   */
  innerRadius?: number;
  /**
   * Show legend
   */
  showLegend?: boolean;
  /**
   * Legend position
   */
  legendPosition?: "right" | "bottom" | "top";
  /**
   * Show data labels on segments
   */
  showLabels?: boolean;
  /**
   * Show percentage labels
   */
  showPercentages?: boolean;
  /**
   * Custom colors for segments
   */
  colors?: string[];
  /**
   * Animation duration
   */
  animationDuration?: number;
  /**
   * Show tooltips on hover
   */
  showTooltips?: boolean;
  /**
   * Format function for values
   */
  formatValue?: (value: number) => string;
  /**
   * Format function for percentages
   */
  formatPercentage?: (percentage: number) => string;
  /**
   * Custom className
   */
  className?: string;
  /**
   * Loading state
   */
  loading?: boolean;
}

/**
 * GlassPieChart component
 * A glassmorphism pie/donut chart with interactive segments and smooth animations
 */
export const GlassPieChart = forwardRef<HTMLDivElement, GlassPieChartProps>(
  function GlassPieChartComponent(
    {
      title,
      data = [],
      size = 300,
      innerRadius = 0,
      showLegend = true,
      legendPosition = "right",
      showLabels = false,
      showPercentages = true,
      colors,
      animationDuration = ANIMATION.DURATION.normal,
      showTooltips = true,
      formatValue,
      formatPercentage,
      loading = false,
      className,
      ...props
    },
    ref
  ) {
    const actualColors = colors || DEFAULT_CHART_COLORS;
    const actualAnimationDuration = animationDuration;
    const actualShowTooltips = showTooltips;
    const actualFormatValue = formatValue || ((value) => value.toString());
    const actualFormatPercentage =
      formatPercentage || ((percentage) => `${Math.round(percentage)}%`);
    const actualLoading = loading;
    const [hoveredSegment, setHoveredSegment] = useState<{
      index: number;
      x: number;
      y: number;
      label: string;
      value: number;
      percentage: number;
    } | null>(null);
    const [hoveredLegendIndex, setHoveredLegendIndex] = useState<number | null>(
      null
    );

    const centerX = size / 2;
    const centerY = size / 2;
    const radius = (size - 40) / 2; // Padding around the chart
    const labelRadius = radius + 30; // Distance for labels

    // Process data for chart
    const processedData = useMemo(() => {
      if (!data || !Array.isArray(data) || data?.length === 0) {
        return { segments: [], total: 0 };
      }

      const sanitizedData = data
        .map((item, index) => ({
          ...item,
          label: item?.label || `Segment ${index + 1}`,
          value: Math.max(0, toFiniteNumber(item?.value)),
        }))
        .filter((item) => item.value > 0);

      const total = sanitizedData.reduce((sum, item) => sum + item.value, 0);
      if (!Number.isFinite(total) || total <= 0) {
        return { segments: [], total: 0 };
      }

      let currentAngle = -Math.PI / 2; // Start from top

      const segments = sanitizedData.map((item, index) => {
        const percentage = (item.value / total) * 100;
        const angle = (item.value / total) * 2 * Math.PI;
        const startAngle = currentAngle;
        const endAngle = currentAngle + angle;

        // Calculate path for segment
        const x1 = centerX + Math.cos(startAngle) * radius;
        const y1 = centerY + Math.sin(startAngle) * radius;
        const x2 = centerX + Math.cos(endAngle) * radius;
        const y2 = centerY + Math.sin(endAngle) * radius;

        const largeArcFlag = angle > Math.PI ? 1 : 0;

        let path = "";
        if (innerRadius > 0) {
          // Donut chart
          const innerX1 = centerX + Math.cos(startAngle) * innerRadius;
          const innerY1 = centerY + Math.sin(startAngle) * innerRadius;
          const innerX2 = centerX + Math.cos(endAngle) * innerRadius;
          const innerY2 = centerY + Math.sin(endAngle) * innerRadius;

          path = `M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2} L ${innerX2} ${innerY2} A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${innerX1} ${innerY1} Z`;
        } else {
          // Pie chart
          path = `M ${centerX} ${centerY} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;
        }

        // Calculate label position
        const labelAngle = startAngle + angle / 2;
        const labelX = centerX + Math.cos(labelAngle) * labelRadius;
        const labelY = centerY + Math.sin(labelAngle) * labelRadius;

        currentAngle = endAngle;

        return {
          ...item,
          path,
          percentage,
          startAngle,
          endAngle,
          labelX,
          labelY,
          color: getSegmentColor(item?.color, actualColors, index),
        };
      });

      return { segments, total };
    }, [
      data,
      centerX,
      centerY,
      radius,
      innerRadius,
      labelRadius,
      actualColors,
    ]);

    // Handle segment hover
    const handleSegmentHover = (
      segment: (typeof processedData.segments)[0],
      event: React.MouseEvent
    ) => {
      if (actualShowTooltips) {
        const rect = event.currentTarget.getBoundingClientRect();
        setHoveredSegment({
          index: processedData.segments.indexOf(segment),
          x: rect.left + rect.width / 2,
          y: rect.top,
          label: segment.label,
          value: segment.value,
          percentage: segment.percentage,
        });
      }
    };

    const handleSegmentLeave = () => {
      setHoveredSegment(null);
    };

    // Calculate legend layout
    const legendItems = processedData.segments.map((segment, index) => ({
      ...segment,
      index,
    }));

    // Loading skeleton
    if (actualLoading) {
      return (
        <GlassCard data-glass-component className={cn("glass-p-6", className)}>
          <div className="glass-animate-pulse glass-gap-4">
            <div className="glass-h-6 glass-surface-subtle/20 glass-radius-md glass-w-48"></div>
            <div className="glass-flex glass-items-center glass-justify-center">
              <div className="glass-w-64 glass-h-64 glass-surface-subtle/10 glass-radius-full"></div>
            </div>
            {showLegend && (
              <div className="glass-flex glass-justify-center glass-gap-4">
                <div className="glass-h-4 glass-surface-subtle/20 glass-radius-md glass-w-20"></div>
                <div className="glass-h-4 glass-surface-subtle/20 glass-radius-md glass-w-20"></div>
                <div className="glass-h-4 glass-surface-subtle/20 glass-radius-md glass-w-20"></div>
              </div>
            )}
          </div>
        </GlassCard>
      );
    }

    return (
      <Motion ref={ref} preset="fadeIn" className="glass-w-full">
        <GlassCard
          className={cn("glass-overflow-hidden", className)}
          {...props}
        >
          {title && (
            <CardHeader>
              <CardTitle className="glass-text-primary glass-text-lg glass-font-semibold">
                {title}
              </CardTitle>
            </CardHeader>
          )}

          <CardContent className="glass-p-4">
            <div
              className={cn(
                "glass-flex",
                legendPosition === "right"
                  ? "glass-flex-row glass-flex-wrap"
                  : "glass-flex-col",
                "glass-items-center glass-justify-center glass-gap-6"
              )}
            >
              {/* Chart */}
              <div className="glass-relative glass-w-full glass-flex-shrink-0 glass-overflow-hidden">
                <svg
                  width="100%"
                  height={size}
                  viewBox={`0 0 ${size} ${size}`}
                  preserveAspectRatio="xMidYMid meet"
                  className="glass-overflow-hidden"
                  style={{ display: "block", maxWidth: size, margin: "0 auto" }}
                >
                  {/* Segments */}
                  {processedData.segments.map((segment, index) => (
                    <g
                      key={`${segment.label}-${index}`}
                      style={{
                        animation: `pieSegment ${actualAnimationDuration}ms ease-out ${index * 100}ms both`,
                      }}
                    >
                      <path
                        d={segment.path}
                        fill={segment.color}
                        stroke="color-mix(in srgb, var(--glass-white) 20%, transparent)"
                        strokeWidth="1"
                        className={`glass-cursor-pointer glass-transition-all glass-duration-[${ANIMATION.DURATION.fast}ms] hover:glass-opacity-80`}
                        onMouseEnter={(e) => handleSegmentHover(segment, e)}
                        onMouseLeave={handleSegmentLeave}
                        style={{
                          animation: `pieSegment ${actualAnimationDuration}ms ease-out ${index * 100}ms both`,
                          opacity:
                            hoveredLegendIndex !== null &&
                            hoveredLegendIndex !== index
                              ? 0.35
                              : 1,
                        }}
                      />

                      {/* Segment labels */}
                      {showLabels && (
                        <text
                          x={segment.labelX}
                          y={segment.labelY}
                          textAnchor={
                            segment.labelX > centerX ? "start" : "end"
                          }
                          className="glass-text-xs glass-font-medium"
                          style={{
                            fill: LABEL_FILL,
                            fontSize: "var(--typography-caption-size)",
                          }}
                        >
                          <ContrastGuard>{segment.label}</ContrastGuard>
                          {showPercentages && (
                            <tspan
                              x={segment.labelX}
                              dy="14"
                              className="glass-text-xs"
                              style={{ fill: LABEL_MUTED_FILL }}
                            >
                              <ContrastGuard>
                                {actualFormatPercentage(segment.percentage)}
                              </ContrastGuard>
                            </tspan>
                          )}
                        </text>
                      )}
                    </g>
                  ))}

                  {/* Center text for donut chart */}
                  {innerRadius > 0 && (
                    <g>
                      <circle
                        cx={centerX}
                        cy={centerY}
                        r={innerRadius}
                        fill="var(--glass-bg-default)"
                        stroke="color-mix(in srgb, var(--glass-white) 20%, transparent)"
                        strokeWidth="1"
                      />
                      <text
                        x={centerX}
                        y={centerY - 5}
                        textAnchor="middle"
                        className="glass-text-sm glass-font-medium"
                        style={{ fill: LABEL_FILL }}
                      >
                        <ContrastGuard>Total</ContrastGuard>
                      </text>
                      <text
                        x={centerX}
                        y={centerY + 15}
                        textAnchor="middle"
                        className="glass-text-lg glass-font-semibold"
                        style={{ fill: CENTER_FILL }}
                      >
                        <ContrastGuard>
                          {actualFormatValue(processedData.total)}
                        </ContrastGuard>
                      </text>
                    </g>
                  )}
                </svg>

                {/* Tooltip */}
                {hoveredSegment && (
                  <Motion preset="fadeIn" className="glass-absolute glass-z-10">
                    <div
                      className={cn(
                        "glass-absolute glass-radius-xl glass-p-3 glass-shadow-xl",
                        "glass-surface-overlay glass-backdrop-blur-md glass-border glass-border-subtle glass-radial-reveal glass-lift"
                      )}
                      style={{
                        left: hoveredSegment.x + 10,
                        top: hoveredSegment.y - 10,
                        transform:
                          hoveredSegment.x > size / 2
                            ? "translateX(-100%)"
                            : "none",
                      }}
                    >
                      <div className="glass-text-primary glass-text-sm">
                        <ContrastGuard>
                          <div className="glass-font-medium">
                            {hoveredSegment.label}
                          </div>
                          <div className="glass-text-primary-glass-opacity-80">
                            {actualFormatValue(hoveredSegment.value)} (
                            {actualFormatPercentage(hoveredSegment.percentage)})
                          </div>
                        </ContrastGuard>
                      </div>
                    </div>
                  </Motion>
                )}
              </div>

              {/* Legend */}
              {showLegend && (legendItems?.length || 0) > 0 && (
                <div
                  className={cn(
                    "glass-flex glass-flex-wrap glass-gap-3",
                    legendPosition === "right"
                      ? "glass-flex-col"
                      : "glass-justify-center"
                  )}
                >
                  {legendItems.map((item) => (
                    <Motion
                      key={item?.label}
                      preset="slideUp"
                      delay={item?.index * 50}
                      className={cn(
                        `glass-flex glass-items-center glass-gap-2 glass-px-2 glass-py-1 glass-radius-md glass-transition-all glass-duration-[${ANIMATION.DURATION.fast}ms] glass-hover--translate-y-0-5`,
                        hoveredLegendIndex !== null &&
                          hoveredLegendIndex !== item?.index
                          ? "glass-opacity-50"
                          : "glass-opacity-100"
                      )}
                      onMouseEnter={() => setHoveredLegendIndex(item?.index)}
                      onMouseLeave={() => setHoveredLegendIndex(null)}
                    >
                      <div
                        className="glass-w-3 glass-h-3 glass-radius-md"
                        style={{ backgroundColor: item?.color }}
                      />
                      <div className="glass-text-sm glass-text-primary-glass-opacity-80">
                        <ContrastGuard>
                          <span className="glass-font-medium">
                            {item?.label}
                          </span>
                          <span className="glass-ml-2 glass-text-primary-glass-opacity-60">
                            {actualFormatValue(item?.value)} (
                            {actualFormatPercentage(item?.percentage)})
                          </span>
                        </ContrastGuard>
                      </div>
                    </Motion>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </GlassCard>
      </Motion>
    );
  }
);

GlassPieChart.displayName = "GlassPieChart";

// Donut Chart variant
export interface GlassDonutChartProps
  extends Omit<GlassPieChartProps, "innerRadius"> {
  /**
   * Inner radius as percentage of outer radius (0-1)
   */
  innerRadiusRatio?: number;
}

export const GlassDonutChart = forwardRef<HTMLDivElement, GlassDonutChartProps>(
  ({ size = 300, innerRadiusRatio = 0.6, ...props }, ref) => {
    const innerRadius = ((size - 40) / 2) * innerRadiusRatio;

    return (
      <GlassPieChart
        ref={ref}
        {...props}
        size={size}
        innerRadius={innerRadius}
      />
    );
  }
);

GlassDonutChart.displayName = "GlassDonutChart";

export default GlassPieChart;
